import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

/** Mirrors pm-api `PracticeAnalyticsSummaryResponse`. */
export interface PracticeAnalyticsSummaryApi {
  tasksTotal: number;
  tasksOverdue: number;
  tasksByStatus: { key: string; count: number }[];
  deadlinesTotal: number;
  activitiesTotal: number;
  tasksRecurringCount?: number;
  tasksOneOffCount?: number;
  tasksHighPriorityCount?: number;
  deadlinesOverdueCount?: number;
  deadlinesDueCount?: number;
  deadlinesSubmittedCount?: number;
  deadlinesMissingCount?: number;
}

export interface AnalyticsBucketApi {
  key: string;
  count: number;
}

export interface TasksPipelineResponseApi {
  priority: AnalyticsBucketApi[];
  byServiceTitle: AnalyticsBucketApi[];
  billingType: AnalyticsBucketApi[];
  recurringCount: number;
  oneOffCount: number;
  workflowStatus: AnalyticsBucketApi[];
  byAssigneeUserId: AnalyticsBucketApi[];
  dueByWeekStartIso: AnalyticsBucketApi[];
  inFlightProgressBuckets: AnalyticsBucketApi[];
  billableMinutesLast4Weeks: number;
  nonBillableMinutesLast4Weeks: number;
}

export interface DeadlineComplianceMonthApi {
  monthKey: string;
  total: number;
  submitted: number;
}

export interface DeadlineTypeComplianceApi {
  deadlineType: string;
  total: number;
  submitted: number;
}

export interface DeadlineUpcomingWeekApi {
  weekStartIso: string;
  count: number;
}

export interface DeadlineAnalyticsResponseApi {
  monthlyCompliance: DeadlineComplianceMonthApi[];
  complianceByType: DeadlineTypeComplianceApi[];
  frequencyBreakdown: AnalyticsBucketApi[];
  upcomingByWeek: DeadlineUpcomingWeekApi[];
}

/** pm-api `TaskSummaryResponse` subset used on dashboard. */
export interface TaskSummaryResponseApi {
  id: number;
  clientUniversalId: string;
  taskName?: string | null;
  dueDate?: string | null;
  status?: number | null;
  priority?: string | null;
  serviceTitle?: string | null;
  userId?: number | null;
  assignedUserIds?: number[];
}

export interface PracticeAuditActivityRowApi {
  id: number;
  clientUniversalId: string;
  taskId?: number | null;
  typeOfActivity?: string | null;
  module?: string | null;
  name?: string | null;
  description?: string | null;
  userId?: number | null;
  createdBy?: number | null;
  createdOnUtc?: string | null;
}

export interface TimeCaptureResponseApi {
  id: number;
  clientUniversalId?: string | null;
  day?: string | null;
  hoursSpent?: string | null;
  status?: string | null;
  taskId?: number | null;
}

export interface PracticeDashboardSummaryTrendsApi {
  tasksTotalChangePercent?: number | null;
  tasksOverdueChangePercent?: number | null;
  deadlinesTotalChangePercent?: number | null;
  activitiesTotalChangePercent?: number | null;
  deadlinesMissingChangePercent?: number | null;
  comparedToLabel?: string | null;
}

/** pm-api `GET /api/v1/practice/dashboard/overview` body. */
export interface PracticeDashboardOverviewApi {
  summary: PracticeAnalyticsSummaryApi;
  recentTasks: TaskSummaryResponseApi[];
  recentDeadlines: import('./pm-deadlines-api.service').DeadlineResponseApi[];
  recentActivity: PracticeAuditActivityRowApi[];
  recentTimeCaptures: TimeCaptureResponseApi[];
  tasksPipeline: TasksPipelineResponseApi | null;
  deadlineAnalytics: DeadlineAnalyticsResponseApi | null;
  summaryTrends?: PracticeDashboardSummaryTrendsApi | null;
  recentActivityTotalCount?: number | null;
}

/** pm-api `DashboardClientTierCountResponse` */
export interface DashboardClientTierCountApi {
  tier: string | null;
  totalCount: number;
  newInPeriod: number;
  changeVsPrior: number;
}

/** pm-api `GET /api/v1/practice/dashboard/client-summary` body. */
export interface PracticeDashboardClientSummaryApi {
  totalCount: number;
  newInPeriod: number;
  totalChangeVsPrior: number;
  byTier: DashboardClientTierCountApi[];
  comparedToLabel: string | null;
}

/** pm-api `GET /api/v1/practice/dashboard/quick-links` body. */
export interface PracticeDashboardQuickLinksApi {
  tasksTotal: number;
  deadlinesTotal: number;
  timesheetsTotal: number;
  clientsTotal: number;
  teamTotal: number;
  invoicesTotal: number;
}

@Injectable({ providedIn: 'root' })
export class PmPracticeDashboardApiService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.pmApiBaseUrl;

  getOverview(params: {
    parentUserId: number;
    actingUserId: number;
    clientUniversalId?: string | null;
    recentTake?: number;
    includeCharts?: boolean;
    /** When false, skips previous-period summary SQL (faster shell load; trends load separately). */
    includeSummaryTrends?: boolean;
    /** Inclusive UTC range. When set, KPI summary totals are cumulative snapshots as of `toUtc`; trends compare snapshots vs the prior window. */
    fromUtc?: string | null;
    toUtc?: string | null;
  }): Observable<PracticeDashboardOverviewApi> {
    let hp = new HttpParams()
      .set('parentUserId', String(params.parentUserId))
      .set('actingUserId', String(params.actingUserId));
    if (params.clientUniversalId?.trim()) {
      hp = hp.set('clientUniversalId', params.clientUniversalId.trim());
    }
    if (params.recentTake != null) {
      hp = hp.set('recentTake', String(params.recentTake));
    }
    if (params.includeCharts === false) {
      hp = hp.set('includeCharts', 'false');
    }
    if (params.includeSummaryTrends === false) {
      hp = hp.set('includeSummaryTrends', 'false');
    }
    if (params.fromUtc?.trim() && params.toUtc?.trim()) {
      hp = hp.set('fromUtc', params.fromUtc.trim()).set('toUtc', params.toUtc.trim());
    }
    return this.http.get<PracticeDashboardOverviewApi>(`${this.base}/api/v1/practice/dashboard/overview`, {
      params: hp
    });
  }

  getQuickLinks(params: {
    parentUserId: number;
    actingUserId: number;
    directoryUserId: number;
    clientUniversalId?: string | null;
    fromUtc?: string | null;
    toUtc?: string | null;
  }): Observable<PracticeDashboardQuickLinksApi> {
    let hp = new HttpParams()
      .set('parentUserId', String(params.parentUserId))
      .set('actingUserId', String(params.actingUserId))
      .set('directoryUserId', String(params.directoryUserId));
    if (params.clientUniversalId?.trim()) {
      hp = hp.set('clientUniversalId', params.clientUniversalId.trim());
    }
    if (params.fromUtc?.trim() && params.toUtc?.trim()) {
      hp = hp.set('fromUtc', params.fromUtc.trim()).set('toUtc', params.toUtc.trim());
    }
    return this.http.get<PracticeDashboardQuickLinksApi>(`${this.base}/api/v1/practice/dashboard/quick-links`, {
      params: hp
    });
  }

  getClientSummary(params: {
    parentUserId: number;
    directoryUserId: number;
    fromUtc?: string | null;
    toUtc?: string | null;
  }): Observable<PracticeDashboardClientSummaryApi> {
    let hp = new HttpParams()
      .set('parentUserId', String(params.parentUserId))
      .set('directoryUserId', String(params.directoryUserId));
    if (params.fromUtc?.trim() && params.toUtc?.trim()) {
      hp = hp.set('fromUtc', params.fromUtc.trim()).set('toUtc', params.toUtc.trim());
    }
    return this.http.get<PracticeDashboardClientSummaryApi>(
      `${this.base}/api/v1/practice/dashboard/client-summary`,
      { params: hp }
    );
  }
}
